<template>
    <div class="mt-4 v-application" :class="{'d-block': !isRestricted}">
        <v-dialog v-model="isPricingDialogOpen" max-width="520">
            <v-card>
                <v-card-title>Preise ändern</v-card-title>
                <v-card-text>
                    <v-alert dense text type="info">
                        Frühere Abrechnungszeiträume bleiben unverändert. Ab dem Startdatum gelten die neuen Konditionen.
                    </v-alert>
                    <v-text-field
                        v-model="pricingForm.effective_date" outlined dense type="date"
                        :min="todayInBerlin()" label="Startdatum" data-test="steffi-pricing-effective-date"
                    ></v-text-field>
                    <v-text-field
                        v-model="pricingForm.monthly_net" outlined dense inputmode="decimal"
                        label="Neuer Monatspreis" suffix="€ netto" data-test="steffi-pricing-monthly"
                    ></v-text-field>
                    <v-text-field
                        v-model="pricingForm.usage_minute_net" outlined dense inputmode="decimal"
                        label="Neuer Minutenpreis" suffix="€ netto" data-test="steffi-pricing-usage"
                    ></v-text-field>
                    <v-text-field
                        v-model="pricingForm.included_monthly_minutes" outlined dense type="number" min="0"
                        label="Freiminuten pro Monat" suffix="Minuten" data-test="steffi-pricing-included-minutes"
                    ></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="isPricingDialogOpen = false">Abbrechen</v-btn>
                    <v-btn
                        color="primary" :loading="isSavingPricing"
                        data-test="steffi-pricing-save" @click="savePricing"
                    >Änderung speichern</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-card v-if="isRestricted" outlined class="mr-3" rounded="lg" data-test="steffi-team-pharmacy-contact">
            <v-card-title>{{ context.pharmacy.name }}</v-card-title>
            <v-card-text>
                <div class="mb-2">
                    <span>Kundennummer:</span>
                    <span class="font-weight-bold body-1">{{ context.pharmacy.customer_id || '-' }}</span>
                </div>
                <div class="d-flex align-center mb-2">
                    <span>E-Mail:</span>
                    <template v-if="isEditingSteffiEmail">
                        <v-text-field
                            v-model="localSteffiEmailDraft"
                            label="Steffi-E-Mail-Adresse"
                            type="email"
                            dense outlined hide-details
                            class="ml-2 mr-1"
                            :rules="[optionalEmailRule]"
                            :disabled="steffiEmailSaving"
                            data-test="steffi-contact-email"
                        />
                        <v-btn
                            icon small color="primary" :loading="steffiEmailSaving"
                            data-test="steffi-contact-email-save" aria-label="Steffi-E-Mail-Adresse speichern"
                            @click="saveSteffiEmail"
                        ><v-icon small>mdi-check</v-icon></v-btn>
                        <v-btn
                            icon small :disabled="steffiEmailSaving"
                            data-test="steffi-contact-email-cancel" aria-label="Bearbeiten abbrechen"
                            @click="cancelSteffiEmailEdit"
                        ><v-icon small>mdi-close</v-icon></v-btn>
                    </template>
                    <template v-else>
                        <span class="font-weight-bold body-1 ml-1" data-test="steffi-contact-email-display">
                            {{ context.pharmacy.steffi_email || context.pharmacy.invitation_email || '-' }}
                        </span>
                        <v-btn
                            icon small data-test="steffi-contact-email-edit"
                            aria-label="Steffi-E-Mail-Adresse bearbeiten" @click="startSteffiEmailEdit"
                        ><v-icon small>mdi-pencil</v-icon></v-btn>
                    </template>
                </div>
                <div class="mb-2">
                    <span>Telefon:</span>
                    <span class="font-weight-bold body-1">{{ context.pharmacy.phone || context.pharmacy.mobile || '-' }}</span>
                </div>
                <div class="mb-2">
                    <span>Angefragt:</span>
                    <span class="font-weight-bold body-1">{{ formatDate(current.requested_at) || '-' }}</span>
                </div>
            </v-card-text>
        </v-card>

        <v-card rounded="lg" outlined class="d-flex mb-3">
            <v-card-text>
                <v-row>
                    <v-col cols="12" sm="5" class="hero">
                        <div class="overline">Paket</div>
                        <div class="mb-1">
                                <v-icon class="mr-1" small>mdi-package-variant-closed</v-icon>
                                <span>Paketname:</span>
                                <span class="font-weight-bold body-1">{{ current.plan_name || current.plan_code || '-' }}</span>
                            </div>
                        <div v-if="!isRestricted">
                            <div class="mb-1">
                                <v-icon class="mr-1" small>mdi-calendar-text-outline</v-icon>
                                <span>Start:</span>
                                <span class="font-weight-bold body-1">{{ formatDate(current.start_date) || '-' }}</span>
                            </div>
                            <div class="mb-2">
                                <v-icon class="mr-1" small>mdi-calendar-text-outline</v-icon>
                                <span>Nächste Abrechnung:</span>
                                <span class="font-weight-bold body-1">{{ formatDate(current.next_billing_date) || '-' }}</span>
                            </div>
                            <div v-if="current.pricing" class="mb-2" data-test="steffi-current-pricing">
                                <div>Einrichtung: <b>{{ formatMoney(current.pricing.setup_net) }} netto</b></div>
                                <div>Monatlich: <b>{{ formatMoney(current.pricing.monthly_net) }} netto</b></div>
                                <div>Minute: <b>{{ formatMoney(current.pricing.usage_minute_net) }} netto</b></div>
                                <div>Freiminuten: <b>{{ current.pricing.included_monthly_minutes || 0 }} pro Monat</b></div>
                            </div>
                            <v-btn
                                v-if="!isRestricted && current.can_change_pricing"
                                small outlined color="primary" data-test="steffi-open-pricing"
                                @click="openPricingDialog"
                            >Preise ändern</v-btn>
                        </div>
                        <v-alert v-if="current.trial_status" type="info" dense outlined class="mt-3 mb-0" data-test="steffi-admin-trial-status">
                            <div>{{ current.trial_expired ? 'Testphase automatisch beendet' : trialStatusLabel(current.trial_status) }}</div>
                            <div v-if="current.trial_started_at">Testbeginn: {{ formatDate(current.trial_started_at) }}</div>
                            <div v-if="current.trial_ends_at">Testende: {{ formatDate(current.trial_ends_at) }}</div>
                            <div v-if="current.trial_cancelled_at">Beendet: {{ formatDate(current.trial_cancelled_at) }}</div>
                            <div v-if="current.trial_cancellation_pending_at" data-test="steffi-trial-cancellation-pending">
                                Beendigung angefordert – technische Deaktivierung bei Famulor ausstehend.
                            </div>
                            <div v-if="!current.trial_expired && (current.trial_status === 'requested' || current.trial_status === 'running')">
                                14 Tage kostenlos, danach automatische Beendigung ohne Kosten.
                                Ein kostenpflichtiges Abonnement muss separat gebucht werden.
                            </div>
                        </v-alert>
                        <v-alert v-if="!isRestricted && current.pending_change" type="info" dense outlined class="mt-3 mb-0" data-test="steffi-pending-pricing">
                            <div>Geplante Preisänderung ab {{ formatDate(current.pending_change.effective_at) }}</div>
                            <template v-if="current.pending_change.pricing">
                                <div>Monatlich: <b>{{ formatMoney(current.pending_change.pricing.monthly_net) }} netto</b></div>
                                <div>Minute: <b>{{ formatMoney(current.pending_change.pricing.usage_minute_net) }} netto</b></div>
                                <div>Freiminuten: <b>{{ current.pending_change.pricing.included_monthly_minutes || 0 }} pro Monat</b></div>
                            </template>
                        </v-alert>
                    </v-col>
                    <v-col cols="12" sm="7" class="hero">
                        <div class="overline">Einrichtung</div>
                        <div class="mb-1">
                            <span class="hero-label">Instanz:</span>
                            <span v-if="current.famulor_assistant_name" class="font-weight-bold body-1">{{ current.famulor_assistant_name }}</span>
                            <span v-else>-</span>
                        </div>
                        <div v-if="current.famulor_assistant_id" class="mb-1">
                            <span class="hero-label">Instanz ID:</span>
                            <span class="font-weight-bold body-1">{{ current.famulor_assistant_id }}</span>
                        </div>
                        <div class="d-flex mt-1 mb-2">
                            <div class="v-application d-block">
                                <span class="hero-label">Steffi Nummer:</span>
                                <a v-if="current.famulor_phone_number" class="font-weight-bold body-1" :href="`tel:${current.famulor_phone_number}`">
                                    {{ current.famulor_phone_number }}
                                </a>
                                <span v-else>-</span>
                            </div>
                        </div>
                        <section>
                            <v-chip
                                small
                                label
                                class="pl-0 mr-2 lighten-5 font-weight-medium"
                                :class="localStatusColor(current.local_subscription_status)"
                                :text-color="localStatusColor(current.local_subscription_status)"
                            >
                                <v-icon :color="localStatusColor(current.local_subscription_status)">
                                    mdi-circle-small
                                </v-icon>
                                <span class="mr-2">FPP Status:</span>
                                <span class="font-weight-bold">{{ localStatusLabel(current.local_subscription_status) }}</span>
                            </v-chip>
                            <v-chip
                                small
                                label
                                class="pl-0 lighten-5 font-weight-medium"
                                :class="famulorStatusColor(current.famulor_operational_status)"
                                :text-color="famulorStatusColor(current.famulor_operational_status)"
                            >
                                <v-icon :color="famulorStatusColor(current.famulor_operational_status)">
                                    mdi-circle-small
                                </v-icon>
                                <span class="mr-2">Technischer Status:</span>
                                <span class="font-weight-bold">{{ famulorStatusLabel(current.famulor_operational_status) }}</span>
                            </v-chip>
                        </section>
                        <div v-if="context.provisioning_diagnostics" class="mt-3">
                            <div class="text-caption text--secondary">Provisionierung</div>
                            <code>{{ diagnosticText }}</code>
                        </div>

                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    small
                                    text
                                    fab
                                    v-bind="attrs"
                                    v-on="on"
                                    color="blue-grey"
                                    class="manual-setup-btn"
                                    data-test="steffi-open-manual-setup"
                                    @click="isSteffiManualSetupDialogOpen = true"
                                >
                                    <v-icon>mdi-cog-outline</v-icon>
                                </v-btn>
                            </template>
                            <span>
                                Manuelle Einrichtung
                            </span>
                        </v-tooltip>
                    </v-col>
                    <v-col
                        v-if="isRestricted && (current.can_cancel || cancellationBlockers.length)"
                        cols="12"
                        class="blocker-alert"
                    >
                        <v-divider />
                        <v-alert
                            v-if="cancellationBlockers.length"
                            type="warning"
                            dense
                            outlined
                            class="mt-3 mb-0"
                            data-test="steffi-cancellation-blockers"
                        >
                            Kündigung derzeit nicht möglich:
                            <div v-for="blocker in cancellationBlockers" :key="blocker" class="font-weight-bold">
                                {{ cancellationBlockerLabel(blocker) }}
                            </div>
                        </v-alert>
                        <div v-else-if="current.can_cancel" class="v-application mt-5 d-flex align-center justify-center">
                            <span class="mr-4 body-2 d-flex align-center">{{ ['requested', 'running'].includes(current.trial_status) ? 'Testphase beenden?' : 'Abonnement kündigen?' }}</span>
                            <v-btn color="red" dark small depressed :loading="isCancellingSteffi" data-test="steffi-admin-cancel" @click="isCancelSteffiDialogOpen = true">
                                <v-icon small dark class="mr-4">mdi-cancel</v-icon>
                                {{ ['requested', 'running'].includes(current.trial_status) ? 'Testphase beenden' : 'stornieren' }}
                            </v-btn>
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <steffi-manual-setup-dialog
            v-model="isSteffiManualSetupDialogOpen"
            :pharmacy-id="pharmacyId"
            :current="current"
            :is-restricted="isRestricted"
            :route-generation="routeGeneration"
            @context-updated="forwardContextUpdated"
        />

        <confirmation-dialog
            v-model="isCancelSteffiDialogOpen"
            :message="['requested', 'running'].includes(current.trial_status) ? 'Testphase endgültig beenden? Es entstehen keine Kosten und kein kostenpflichtiges Abonnement. Thomas wird zur technischen Deaktivierung informiert.' : 'Soll das Steffi-Abonnement wirklich gekündigt werden? Diese Aktion kann nicht rückgängig gemacht werden.'"
            @save="cancelSteffi"
        />

        <v-row v-if="canViewCommercialData" dense data-test="steffi-admin-commercial-data">
            <v-col cols="12" md="4">
                <v-card outlined rounded="lg" class="mb-3">
                    <v-card-title class="overline">Rechnungen</v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col cols="4">
                                <v-alert
                                    dense
                                    text
                                    color="blue darken-2"
                                    class="mb-0 text-center"
                                >
                                    <h5 class="text-h4 font-weight-bold">{{ invoiceSummary.open_count }}</h5>
                                    offen
                                </v-alert>
                            </v-col>
                            <v-col cols="4">
                                <v-alert
                                    dense
                                    text
                                    color="green darken-2"
                                    class="mb-0 text-center"
                                >
                                    <h5 class="text-h4 font-weight-bold">{{ invoiceSummary.paid_count }}</h5>
                                    bezahlt
                                </v-alert>
                            </v-col>
                            <v-col cols="4">
                                <v-alert
                                    dense
                                    text
                                    color="blue-grey darken-2"
                                    class="mb-0 text-center"
                                >
                                    <h5 class="text-h4 font-weight-bold">{{ invoiceSummary.created_count }}</h5>
                                    vorbereitet
                                </v-alert>
                            </v-col>
                        </v-row>
                        <v-divider v-if="invoiceSummary.latest_paid_at" class="my-4" />
                        <div v-if="invoiceSummary.latest_paid_at">
                            <span>Letzte Zahlung:</span>
                            <span class="font-weight-bold body-1">{{ formatDate(invoiceSummary.latest_paid_at) }}</span>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" md="4">
                <v-card outlined rounded="lg" class="mb-3" data-test="steffi-usage">
                    <v-card-title class="overline">Nutzung</v-card-title>
                    <v-card-text>
                        <div v-if="usageSync.state === 'not_applicable'" class="caption"
                             data-test="steffi-usage-sync" :data-state="usageSync.state">
                            {{ usageSyncLabel(usageSync.state) }}
                        </div>
                        <v-alert
                            v-else
                            dense outlined :type="usageSync.state === 'fresh' ? 'success' : 'warning'"
                            data-test="steffi-usage-sync" :data-state="usageSync.state"
                        >
                            <div>{{ usageSyncLabel(usageSync.state) }}</div>
                            <div v-if="!usageSync.last_success_at && usageSync.state !== 'never_synced'">
                                Nutzungsdaten noch nicht synchronisiert
                            </div>
                            <div v-if="usageSync.last_success_at" class="caption">
                                Letzte erfolgreiche Synchronisierung: {{ formatUsageSyncDate(usageSync.last_success_at) }}
                            </div>
                        </v-alert>
                        <v-row v-if="usageSync.last_success_at || usageSummary.total_call_count" class="v-application" data-test="steffi-usage-counts">
                            <v-col v-if="usageSync.state !== 'fresh'" cols="12" class="pb-0 caption">
                                Bekannte Nutzung (unvollständig)
                            </v-col>
                            <v-col cols="12" sm="6" class="d-flex pb-0">
                                <v-avatar size="26" color="teal lighten-5" class="px-5 py-5 mr-3">
                                    <v-icon color="teal">mdi-phone</v-icon>
                                </v-avatar>
                                <section :class="{'status-card-info': $vuetify.breakpoint.xsOnly}">
                                    <h3>{{ usageSummary.total_call_count }}</h3>
                                    <p class="mb-0 caption grey--text text--darken-1">Calls</p>
                                </section>
                            </v-col>
                            <v-col cols="12" sm="6" class="d-flex pb-0">
                                <v-avatar size="26" color="purple lighten-5" class="px-5 py-5 mr-3">
                                    <v-icon color="purple">mdi-clock-outline</v-icon>
                                </v-avatar>
                                <section :class="{'status-card-info': $vuetify.breakpoint.xsOnly}">
                                    <h3>{{ usageSummary.total_billable_minutes }}</h3>
                                    <p class="mb-0 caption grey--text text--darken-1">Minuten</p>
                                </section>
                            </v-col>
                            <v-col cols="12" sm="6" class="d-flex">
                                <v-avatar size="26" color="orange lighten-5" class="px-5 py-5 mr-3">
                                    <v-icon color="orange">mdi-phone-clock</v-icon>
                                </v-avatar>
                                <section :class="{'status-card-info': $vuetify.breakpoint.xsOnly}">
                                    <h3>{{ usageSummary.pending_policy_count }}</h3>
                                    <p class="mb-0 caption grey--text text--darken-1">Pending</p>
                                </section>
                            </v-col>
                            <v-col cols="12" sm="6" class="d-flex">
                                <v-avatar size="26" color="red lighten-5" class="px-5 py-5 mr-3">
                                    <v-icon color="red">mdi-phone-cancel-outline</v-icon>
                                </v-avatar>
                                <section :class="{'status-card-info': $vuetify.breakpoint.xsOnly}">
                                    <h3>{{ usageSummary.non_billable_count }}</h3>
                                    <p class="mb-0 caption grey--text text--darken-1">nicht abrechenbar</p>
                                </section>
                            </v-col>
                        </v-row>
                        <v-divider v-if="usageSummary.latest_occurred_at" class="my-4" />
                        <div v-if="usageSummary.latest_occurred_at">
                            <span>Letzte Nutzung:</span>
                            <span class="font-weight-bold body-1">{{ formatDate(usageSummary.latest_occurred_at) }}</span>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" md="4">
                <v-card outlined rounded="lg" class="mb-3">
                    <v-card-title class="overline">Legal</v-card-title>
                    <v-card-text v-if="legalAcceptance">
                        <div class="d-flex justify-space-between">
                            <span>Akzeptiert:</span>
                            <span class="font-weight-bold">{{ formatDate(legalAcceptance.accepted_at) || '-' }}</span>
                        </div>

                        <v-divider class="my-2" />

                        <div class="d-flex justify-space-between">
                            <span>User:</span>
                            <span class="font-weight-bold">{{ legalAcceptance.accepted_by_display || legalAcceptance.accepted_by || legalAcceptance.customer_user_id }}</span>
                        </div>

                        <v-divider class="my-2" />

                        <div class="d-flex justify-space-between">
                            <span>Vertragsset angenommen:</span>
                            <v-chip
                                small
                                label
                                :color="legalAcceptance.accepted_contract_terms ? 'green' : 'red'"
                                :text-color="legalAcceptance.accepted_contract_terms ? 'green' : 'red'"
                                class="lighten-5"
                            >
                                <v-icon small class="mr-1">{{ legalAcceptance.accepted_contract_terms ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline-red' }}</v-icon>
                                <span class="font-weight-bold">{{ legalAcceptance.accepted_contract_terms ? 'Ja' : 'Nein' }}</span>
                            </v-chip>
                        </div>

                        <v-divider v-if="legalAcceptance.accepted_contract_terms" class="my-2" />

                        <div v-if="legalAcceptance.contract_version" class="d-flex justify-space-between">
                            <span>Version:</span>
                            <span class="font-weight-bold">{{ legalAcceptance.contract_version }}</span>
                        </div>

                        <v-divider v-if="legalAcceptance.accepted_contract_terms" class="my-2" />

                        <div class="d-flex justify-space-between">
                            <v-btn
                                v-if="legalAcceptance.contract_url"
                                :href="legalAcceptance.contract_url"
                                target="_blank"
                                link
                                outlined
                                rel="noopener"
                                x-small
                                color="primary"
                            >
                                <v-icon small>mdi-arrow-down-bold</v-icon>
                                Vertragsset öffnen
                            </v-btn>
                            <v-btn
                                v-if="legalAcceptance.agb_url"
                                :href="legalAcceptance.agb_url"
                                target="_blank"
                                link
                                outlined
                                rel="noopener"
                                x-small
                                color="primary"
                            >
                                <v-icon small>mdi-arrow-down-bold</v-icon>
                                Nutzungsbedingungen öffnen
                            </v-btn>
                        </div>
                    </v-card-text>
                    <v-card-text v-else>
                        <span class="text--secondary">Keine Legal-Akzeptanz gespeichert.</span>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col v-if="canViewCommercialData" dense data-test="steffi-admin-recent-data" cols="12">
                <v-card outlined class="mb-3">
                    <v-card-title class="overline">Letzte Rechnungen</v-card-title>
                    <v-divider />
                    <v-simple-table>
                        <thead>
                            <tr>
                                <th>Ref</th>
                                <th>Typ</th>
                                <th>Status</th>
                                <th>Zeitraum</th>
                                <th>Summe</th>
                                <th>Erstellt</th>
                                <th>Bezahlt am</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="invoice in latestInvoices" :key="invoice.id">
                                <td>{{ invoice.ref || '-' }}</td>
                                <td>{{ invoice.invoice_kind_label || invoice.invoice_kind || '-' }}</td>
                                <td>{{ invoice.status_label || invoice.status || '-' }}</td>
                                <td>{{ invoice.billing_period_label || '-' }}</td>
                                <td>{{ invoice.uploaded_legacy_invoice ? '-' : formatInvoiceTotal(invoice.total) }}</td>
                                <td>{{ formatDate(invoice.created_at) || '-' }}</td>
                                <td>{{ formatDate(invoice.date_paid) || '-' }}</td>
                                <td>
                                    <v-btn link text fab small :href="invoice.download_url || `${apobilanzOriginUrl}/product-invoices/${invoice.id}`" target="_blank" rel="noopener" color="primary">
                                        <v-icon small>mdi-file-link-outline</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                            <tr v-if="!latestInvoices.length">
                                <td colspan="7" class="text--secondary">Keine Produktrechnungen gespeichert.</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col
                v-if="canViewHistory"
                data-test="steffi-admin-history"
                cols="12"
                :md="canViewRecentReports ? 6 : 12"
            >
                <v-card outlined class="mb-3">
                    <v-tabs v-model="historyTab" dense class="mb-1">
                        <v-tab>Ereignisse</v-tab>
                        <v-tab>Abonnementverlauf</v-tab>
                    </v-tabs>
                    <v-divider />
                    <v-tabs-items v-model="historyTab">
                        <v-tab-item>
                            <v-card-text class="history-container">
                                <v-timeline
                                    v-if="context.events.length"
                                    align-top
                                    dense
                                    data-test="steffi-admin-events-timeline"
                                >
                                    <v-timeline-item
                                        v-for="event in context.events"
                                        :key="event.id || `${event.event_type}-${event.effective_at}`"
                                        :color="eventMeta(event.event_type)?.color"
                                        small
                                    >
                                        <v-row class="pt-1">
                                            <v-col cols="12" sm="9" class="pt-2 pb-1 pb-sm-3">
                                                <strong>{{ eventMeta(event.event_type)?.label }}</strong>
                                                <div class="text-caption">
                                                    {{ actorLabel(event.actor_type) }}
                                                </div>
                                            </v-col>
                                            <v-col cols="12" sm="3" class="font-weight-medium pt-0 pt-sm-2">
                                                {{ formatDate(event.effective_at || event.created_at) || '-' }}
                                            </v-col>
                                        </v-row>
                                    </v-timeline-item>
                                </v-timeline>
                                <p v-else class="text--secondary mb-0">Keine Events gespeichert.</p>
                            </v-card-text>
                        </v-tab-item>
                        <v-tab-item>
                            <v-simple-table>
                                <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th>Paket</th>
                                        <th>Start</th>
                                        <th>Ende</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in context.subscription_history" :key="item.subscription_id">
                                        <td>{{ localStatusLabel(item.status) }}</td>
                                        <td>{{ item.plan_name || item.plan_code }}</td>
                                        <td>{{ formatDate(item.start_date) || '-' }}</td>
                                        <td>{{ formatDate(item.end_date || item.cancelled_at) || '-' }}</td>
                                    </tr>
                                    <tr v-if="!context.subscription_history.length">
                                        <td colspan="4" class="text--secondary">Keine Historie gespeichert.</td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                            <v-divider></v-divider>
                            <v-simple-table data-test="steffi-pricing-history">
                                <thead>
                                    <tr>
                                        <th>Preis gültig ab</th>
                                        <th>Gültig bis</th>
                                        <th>Monat netto</th>
                                        <th>Minute netto</th>
                                        <th>Freiminuten/Monat</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in context.pricing_history" :key="`${item.start_at}-${index}`">
                                        <td>{{ formatDate(item.start_at) || '-' }}</td>
                                        <td>{{ formatDate(item.end_at) || 'aktuell' }}</td>
                                        <td>{{ formatMoney(item.pricing.monthly_net) }}</td>
                                        <td>{{ formatMoney(item.pricing.usage_minute_net) }}</td>
                                        <td>{{ item.pricing.included_monthly_minutes || 0 }}</td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </v-tab-item>
                    </v-tabs-items>
                </v-card>
            </v-col>

            <v-col v-if="canViewCommercialData && canViewRecentReports" dense data-test="steffi-admin-recent-data" cols="12" md="6">
                <v-card outlined class="mb-3">
                    <v-card-title class="overline">Letzte Berichte</v-card-title>
                    <v-divider />
                    <v-simple-table>
                        <thead>
                            <tr>
                                <th>Ref</th>
                                <th>Typ</th>
                                <th>Status</th>
                                <th>Dauer</th>
                                <th>Start</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="report in recentReports" :key="report.id || report.usage_event_id">
                                <td>{{ report.reference || '-' }}</td>
                                <td>{{ report.source_type || '-' }}</td>
                                <td>{{ report.status || '-' }}</td>
                                <td>{{ report.duration_seconds || 0 }}s</td>
                                <td>{{ formatDate(report.started_at) || '-' }}</td>
                            </tr>
                            <tr v-if="!recentReports.length">
                                <td colspan="5" class="text--secondary">Keine Berichte gespeichert.</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>
<script>
import axios from 'axios';
import { mapMutations } from 'vuex';
import { validators } from '../../../validators';
import SteffiManualSetupDialog from './SteffiManualSetupDialog.vue';
import ConfirmationDialog from '../../appointments/ConfirmationDialog.vue';

export default {
    name: 'SteffiDataView',
    components: {
        SteffiManualSetupDialog,
        ConfirmationDialog,
    },
    props: {
        pharmacyId: {
            type: String,
            required: true,
        },
        context: {
            type: Object,
            default: null,
        },
        routeGeneration: {
            type: Number,
            required: true,
        },
        steffiEmailSaving: {
            type: Boolean,
            default: false,
        },
        steffiEmailSaveSuccess: {
            type: Object,
            default: null,
        },
    },
    emits: [
        'context-updated',
        'save-steffi-email',
    ],
    data: () => ({
        historyTab: 0,
        isCancelSteffiDialogOpen: false,
        isCancellingSteffi: false,
        isSteffiManualSetupDialogOpen: false,
        isPricingDialogOpen: false,
        isSavingPricing: false,
        pricingForm: { effective_date: '', monthly_net: '', usage_minute_net: '', included_monthly_minutes: 0 },
        isEditingSteffiEmail: false,
        localSteffiEmailDraft: '',
        steffiEmailSaveRequestId: 0,
        pendingSteffiEmailSave: null,
        isActive: true,
    }),
    computed: {
        hasSteffiData() {
            return Boolean(this.context && this.context.has_steffi_current_or_history);
        },
        isRestricted() {
            return this.context?.access_scope === 'steffi_team';
        },
        canViewCommercialData() {
            const capabilities = this.context?.capabilities || {};
            return Boolean(capabilities.view_billing || capabilities.view_usage || capabilities.view_legal);
        },
        canViewHistory() {
            const capabilities = this.context?.capabilities || {};
            return Boolean(capabilities.view_events);
        },
        canViewRecentReports() {
            return Boolean(this.context?.usage?.recent_reports.length);
        },
        apobilanzOriginUrl() {
            const protocol = window.location.protocol;
            const adminHost = window.location.host;
            const apobilanzHost = adminHost.startsWith('admin.')
                ? adminHost.replace(/^admin\./, 'apobilanz.')
                : 'apobilanz.fp.local';

            return `${protocol}//${apobilanzHost}`;
        },
        current() {
            return this.context?.current || {};
        },
        pharmacy() {
            return this.context?.pharmacy || {};
        },
        invoiceSummary() {
            return this.context?.billing?.invoice_summary || {};
        },
        latestInvoices() {
            return this.context?.billing?.latest_invoices || [];
        },
        usageSummary() {
            return this.context?.usage?.usage_summary || {};
        },
        usageSync() {
            return this.context?.usage?.usage_sync || {};
        },
        recentReports() {
            return this.context?.usage?.recent_reports || [];
        },
        legalAcceptance() {
            return this.context?.legal_acceptance;
        },
        diagnosticText() {
            if (!this.context?.provisioning_diagnostics) return '';
            return JSON.stringify(this.context.provisioning_diagnostics);
        },
        cancellationBlockers() {
            return Array.isArray(this.current?.cancellation_blockers)
                ? this.current.cancellation_blockers
                : [];
        },
    },
    methods: {
        usageSyncLabel(state) {
            return {
                not_applicable: 'Keine nutzungsrelevante Buchung.',
                never_synced: 'Nutzungsdaten noch nicht synchronisiert',
                fresh: 'Nutzungsdaten aktuell',
                stale: 'Nutzungsdaten veraltet',
                failed: 'Synchronisierung fehlgeschlagen',
            }[state];
        },
        formatUsageSyncDate(date) {
            return new Date(date).toLocaleString('de-DE', {
                timeZone: 'Europe/Berlin',
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        },
        startSteffiEmailEdit() {
            this.localSteffiEmailDraft = this.context?.pharmacy?.steffi_email
                || this.context?.pharmacy?.invitation_email
                || '';
            this.isEditingSteffiEmail = true;
            this.pendingSteffiEmailSave = null;
        },
        cancelSteffiEmailEdit() {
            this.isEditingSteffiEmail = false;
            this.pendingSteffiEmailSave = null;
        },
        saveSteffiEmail() {
            if (this.optionalEmailRule(this.localSteffiEmailDraft) !== true) return;
            const request = {
                email: this.localSteffiEmailDraft,
                pharmacyId: this.pharmacyId,
                routeGeneration: this.routeGeneration,
                requestId: ++this.steffiEmailSaveRequestId,
            };
            this.pendingSteffiEmailSave = request;
            this.$emit('save-steffi-email', request);
        },
        optionalEmailRule(value) {
            return !String(value || '').trim() || validators.email(value);
        },
        isCurrentAction(pharmacyId, routeGeneration) {
            return this.isActive
                && this.pharmacyId === pharmacyId
                && this.routeGeneration === routeGeneration;
        },
        forwardContextUpdated(context, pharmacyId, routeGeneration) {
            if (!this.isCurrentAction(pharmacyId, routeGeneration)) return;
            this.$emit('context-updated', context, pharmacyId, routeGeneration);
        },
        formatDate(date) {
            if (!date) return null;
            return this.$formatDate(date, 'DD.MM.YYYY HH:mm');
        },
        formatInvoiceTotal(total) {
            if (total === null || total === undefined || total === '') return '-';
            return `${String(total).replace('.', ',')} EUR`;
        },
        formatMoney(value) {
            if (value === null || value === undefined || value === '') return '-';
            return `${String(value).replace('.', ',')} €`;
        },
        openPricingDialog() {
            const pending = this.current.pending_change;
            const pricing = pending?.pricing || this.current.pricing;
            this.pricingForm.effective_date = pending?.effective_at?.slice(0, 10) || this.todayInBerlin();
            this.pricingForm.monthly_net = pricing?.monthly_net || '';
            this.pricingForm.usage_minute_net = pricing?.usage_minute_net || '';
            this.pricingForm.included_monthly_minutes = pricing?.included_monthly_minutes || 0;
            this.isPricingDialogOpen = true;
        },
        todayInBerlin() {
            return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
        },
        async savePricing() {
            if (this.isSavingPricing) return;
            const pharmacyId = this.pharmacyId;
            const routeGeneration = this.routeGeneration;
            try {
                this.isSavingPricing = true;
                const response = await axios.put(
                    `/api/pharmacies/${pharmacyId}/steffi/pricing`,
                    this.pricingForm,
                );
                if (!this.isCurrentAction(pharmacyId, routeGeneration)) return;
                this.$emit('context-updated', response.data, pharmacyId, routeGeneration);
                this.isPricingDialogOpen = false;
                this.alertSuccess('Die neuen Steffi-Preise wurden gespeichert.');
            } catch (error) {
                if (!this.isCurrentAction(pharmacyId, routeGeneration)) return;
                this.alertError(error?.response?.data?.message || 'Die Preise konnten nicht geändert werden.');
            } finally {
                if (this.isCurrentAction(pharmacyId, routeGeneration)) this.isSavingPricing = false;
            }
        },
        localStatusLabel(status) {
            return {
                active: 'Aktiv',
                pending_onboarding: 'Onboarding offen',
                awaiting_phone_number: 'Telefonnummer offen',
                provisioning: 'Provisionierung',
                provisioning_failed: 'Fehler',
                paused: 'Pausiert',
                cancelled: 'Gekündigt',
                historical: 'Historisch',
            }[status] || status || 'Unbekannt';
        },
        trialStatusLabel(status) {
            return { requested: 'Testphase angefragt', running: 'Testphase läuft', cancelled: 'Testphase beendet', expired: 'Testphase automatisch beendet', converted: 'Testphase abgeschlossen – kostenpflichtiges Abonnement' }[status] || status;
        },
        localStatusColor(status) {
            return {
                active: 'green',
                pending_onboarding: 'blue',
                awaiting_phone_number: 'orange',
                provisioning: 'blue',
                provisioning_failed: 'red',
                paused: 'orange',
                cancelled: 'blue-grey',
                historical: 'grey',
            }[status] || 'grey';
        },
        famulorStatusLabel(status) {
            return {
                active: 'Aktiv',
                inactive: 'Inaktiv',
                unknown: 'Unbekannt',
            }[status] || 'Unbekannt';
        },
        famulorStatusColor(status) {
            return {
                active: 'green',
                inactive: 'red',
                unknown: 'blue-grey',
            }[status] || 'blue-grey';
        },
        cancellationBlockerLabel(blocker) {
            return {
                famulor_assistant_still_operational:
                    'Der Famulor-Assistent ist noch betriebsbereit. Bitte deaktivieren Sie den Assistenten, bevor das Abonnement gekündigt werden kann.',
                provider_deactivation_pending:
                    'Die Beendigung wurde angefordert und wartet auf die bestätigte Deaktivierung des Famulor-Assistenten.',
            }[blocker] || blocker;
        },
        eventMeta(value) {
            const map = {
                trial_requested: { label: 'Testphase angefragt', color: 'blue darken-2', icon: 'check' },
                trial_started: { label: 'Testphase gestartet', color: 'green', icon: 'check' },
                trial_cancelled: { label: 'Testphase beendet', color: 'red', icon: 'cancel' },
                trial_expired: { label: 'Testphase automatisch beendet', color: 'grey', icon: 'schedule' },
                trial_converted: { label: 'Kostenpflichtiges Abonnement begonnen', color: 'green', icon: 'check' },
                admin_invitation_sent: { label: 'Einladung gesendet', color: 'blue darken-2', icon: 'mail' },
                admin_invitation_pending: { label: 'Einladung offen', color: 'amber', icon: 'mail' },
                admin_invitation_accepted: { label: 'Einladung akzeptiert', color: 'green', icon: 'check' },
                legacy_contract_imported: { label: 'Bestandsvertrag importiert', color: 'green', icon: 'check' },
                legal_accepted: { label: 'Vertragsbedingungen akzeptiert', color: 'green', icon: 'check' },
                onboarding_requested: { label: 'Onboarding angefragt', color: 'amber', icon: 'phone' },
                onboarding_notification_sent: { label: 'Onboarding-Team informiert', color: 'blue darken-2', icon: 'mail' },
                onboarding_notification_failed: { label: 'Onboarding-Mail fehlgeschlagen', color: 'red', icon: 'cancel' },
                booking_submitted: { label: 'Buchung eingereicht', color: 'blue darken-2', icon: 'check' },
                booking_confirmation_sent: { label: 'Buchungsbestätigung gesendet', color: 'blue darken-2', icon: 'mail' },
                booking_confirmation_failed: { label: 'Buchungsbestätigung fehlgeschlagen', color: 'red', icon: 'cancel' },
                setup_invoice_created: { label: 'Einrichtungsrechnung erstellt', color: 'blue darken-2', icon: 'check' },
                payment_status_changed: { label: 'Zahlungsstatus geändert', color: 'blue darken-2', icon: 'update' },
                provisioning_requested: { label: 'Einrichtung angefragt', color: 'amber', icon: 'phone' },
                created: { label: 'Erstellt', color: 'blue darken-2', icon: 'check' },
                marked_ready: { label: 'Bereit markiert', color: 'green', icon: 'check' },
                provisioning_failed: { label: 'Einrichtung fehlgeschlagen', color: 'red', icon: 'cancel' },
                activated: { label: 'Aktiviert', color: 'green', icon: 'check' },
                reactivated: { label: 'Reaktiviert', color: 'green', icon: 'update' },
                upgrade_requested: { label: 'Änderung gebucht', color: 'amber', icon: 'mail' },
                upgraded: { label: 'Paket gewechselt', color: 'green', icon: 'check' },
                pricing_changed: { label: 'Preise geändert', color: 'green', icon: 'update' },
                paused: { label: 'Pausiert', color: 'amber', icon: 'pause' },
                cancelled: { label: 'Gekündigt', color: 'red', icon: 'cancel' },
                customer_access_email_sent: { label: 'Steffi-Zugang gesendet', color: 'blue darken-2', icon: 'mail' },
                customer_access_email_failed: { label: 'Steffi-Zugangsmail fehlgeschlagen', color: 'red', icon: 'cancel' },
                famulor_status_synchronized: { label: 'Technischer Steffi-Status aktualisiert', color: 'blue darken-2', icon: 'update' },
                manual_setup_updated: { label: 'Manuelle Einrichtung aktualisiert', color: 'green', icon: 'update' },
                assistant_mapping_updated: { label: 'Assistant-Mapping aktualisiert', color: 'green', icon: 'update' },
                phone_mapping_updated: { label: 'Telefon-Mapping aktualisiert', color: 'green', icon: 'update' },
            }
            return map[value] || { label: value || '-', color: 'blue-grey' }
        },
        actorLabel(value) {
            const map = {
                customer: 'Kunde',
                admin: 'Administration',
                system: 'System',
                integration: 'Integration'
            }
            return map[value] || value || '-'
        },

        async cancelSteffi() {
            if (!this.pharmacyId || this.isCancellingSteffi) return;
            const pharmacyId = this.pharmacyId;
            const routeGeneration = this.routeGeneration;
            try {
                this.isCancellingSteffi = true;
                const response = await axios.post(`/api/pharmacies/${pharmacyId}/steffi/cancel`);
                if (!this.isCurrentAction(pharmacyId, routeGeneration)) return;
                this.$emit("context-updated", response.data, pharmacyId, routeGeneration);
                if (response.data?.current?.trial_cancellation_pending_at) {
                    this.alertSuccess("Beendigung angefordert. Sie wird nach bestätigter Famulor-Deaktivierung abgeschlossen.");
                } else {
                    this.alertSuccess("Steffi-Abonnement wurde gekündigt.");
                }
            } catch (error) {
                if (!this.isCurrentAction(pharmacyId, routeGeneration)) return;
                this.alertError(error?.response?.data?.message || "Steffi konnte nicht gekündigt werden.");
            } finally {
                if (this.isCurrentAction(pharmacyId, routeGeneration)) {
                    this.isCancellingSteffi = false;
                    this.isCancelSteffiDialogOpen = false;
                }
            }
        },
        ...mapMutations({
            alertSuccess: "alert/success",
            alertError: "alert/error",
        }),
    },

    watch: {
        steffiEmailSaveSuccess(value) {
            if (
                !value
                || !this.pendingSteffiEmailSave
                || value.pharmacyId !== this.pharmacyId
                || value.routeGeneration !== this.routeGeneration
                || value.requestId !== this.pendingSteffiEmailSave.requestId
            ) return;
            this.localSteffiEmailDraft = value.email || '';
            this.pendingSteffiEmailSave = null;
            this.isEditingSteffiEmail = false;
        },
        routeGeneration() {
            this.isSavingPricing = false;
            this.isPricingDialogOpen = false;
            this.isCancellingSteffi = false;
            this.isCancelSteffiDialogOpen = false;
            this.isSteffiManualSetupDialogOpen = false;
            this.isEditingSteffiEmail = false;
            this.pendingSteffiEmailSave = null;
        },
    },

    beforeDestroy() {
        this.isActive = false;
    },

}
</script>

<style scoped>

.steffi-profile-tab code {
    white-space: normal;
    word-break: break-word;
}
.hero .hero-label {
    font-size: 1.1em;
}
.history-container {
    max-height: 25em;
    overflow: auto;
}
.vuetify-wrapper .v-application .overline {
    font-size: 0.95rem !important;
    line-height: 1rem !important;
    margin-bottom: 1rem;
    font-weight: bold;
    color: rgba(0, 0, 0, 0.777) !important
}
.v-application .hero .overline {
    margin-bottom: 1.5rem;
}
.status-card-info {
    width: 5rem;
    white-space: nowrap;
}
.vuetify-wrapper .v-timeline:before {
    left: 2.95rem;
}
.manual-setup-btn {
    position: absolute;
    top: 0;
    right: 0;
}

::v-deep th {
  background-color: #f2f2f2 !important;
}
::v-deep td {
    font-size: 0.9rem !important;
}
.vuetify-wrapper .v-card {
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 2px 6px rgba(15, 23, 42, 0.04) !important;
}
.blocker-alert {
    width: 18rem;
}
</style>
